import { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;
const CATEGORIES = ['General', 'Study Abroad', 'Work Placements', 'Payments', 'Visa Process'];

const emptyForm = { category: 'General', question: '', answer: '', order: 0, isPublished: true };

export default function AdminFaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');

  const token = localStorage.getItem('adminToken');

  const fetchFaqs = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/faqs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setFaqs(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchFaqs, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (faq) => {
    setEditingId(faq.id);
    setForm({ category: faq.category, question: faq.question, answer: faq.answer, order: faq.order, isPublished: faq.isPublished });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const url = editingId ? `${API_BASE}/admin/faqs/${editingId}` : `${API_BASE}/admin/faqs`;
    await fetch(url, {
      method: editingId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form),
    });
    setSaving(false);
    setModalOpen(false);
    fetchFaqs();
  };

  const handleTogglePublished = async (id) => {
    await fetch(`${API_BASE}/admin/faqs/${id}/toggle-published`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchFaqs();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    await fetch(`${API_BASE}/admin/faqs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchFaqs();
  };

  const filtered = activeCategory === 'All' ? faqs : faqs.filter((f) => f.category === activeCategory);
  const publishedCount = faqs.filter((f) => f.isPublished).length;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <header className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">FAQ Management</h2>
            <p className="text-[#64748b] mt-1">{publishedCount} of {faqs.length} questions published to the public FAQ page.</p>
          </div>
          <button
            onClick={openCreate}
            className="text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-md hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <span className="material-symbols-outlined">add</span>
            Add Question
          </button>
        </header>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['All', ...CATEGORIES].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                activeCategory === cat ? 'bg-[#00255a] text-white' : 'bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#00255a]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-[#64748b] py-20">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#64748b] py-20">No FAQs in this category yet.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((faq) => (
              <div key={faq.id} className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase text-[#365da3] bg-[#365da3]/10 px-2 py-1 rounded">{faq.category}</span>
                    {!faq.isPublished && (
                      <span className="text-[10px] font-bold uppercase text-[#747781] bg-[#e0e3e5] px-2 py-1 rounded">Hidden</span>
                    )}
                  </div>
                  <p className="font-bold text-[#0f172a] mb-1">{faq.question}</p>
                  <p className="text-sm text-[#64748b] line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleTogglePublished(faq.id)} className="p-2 text-[#64748b] hover:bg-[#f2f4f6] rounded-full" title={faq.isPublished ? 'Hide' : 'Publish'}>
                    <span className="material-symbols-outlined text-[20px]">{faq.isPublished ? 'visibility' : 'visibility_off'}</span>
                  </button>
                  <button onClick={() => openEdit(faq)} className="p-2 text-[#00255a] hover:bg-[#f2f4f6] rounded-full">
                    <span className="material-symbols-outlined text-[20px]">edit</span>
                  </button>
                  <button onClick={() => handleDelete(faq.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full">
                    <span className="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModalOpen(false)}>
            <form onSubmit={handleSave} className="bg-white rounded-2xl p-8 w-full max-w-lg shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-[#00255a] mb-2">{editingId ? 'Edit Question' : 'Add Question'}</h3>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Question</label>
                <input
                  required
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Answer</label>
                <textarea
                  required
                  rows="4"
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10 resize-none"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                  className="w-5 h-5"
                />
                <label className="text-sm text-[#44474f]">Published (visible on public FAQ page)</label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Question'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}